/**
 * Qualitative / Descriptor (DUS) — crop descriptor library.
 *
 * Renders ABOVE the upload card on the Qualitative / Descriptor (DUS)
 * tab. Lets a researcher browse the official PPV&FR (India) qualitative /
 * pseudo-qualitative descriptor list for a notified crop and load a
 * matching demo table in one click. The DUS engine itself is crop-agnostic
 * — these are standardised templates, not a restriction.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Leaf, Sprout, Table2, Download, ChevronDown, Info, Loader2 } from 'lucide-react';
import { API } from '@/App';

const TEAL = '#0891B2';
const INK = '#0E3A46';

const TYPE_LABEL = { QL: 'Qualitative', PQ: 'Pseudo-qualitative', QN: 'Quantitative' };

export default function QualitativeDUSLanding({ designKey = 'qualitative-dus' }) {
  const [crops, setCrops] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loadingKey, setLoadingKey] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/dus-crops`)
      .then(({ data }) => setCrops(data.crops || []))
      .catch(() => setError(true));
  }, []);

  const active = crops.find(c => c.key === selected) || null;

  const loadSample = async (crop) => {
    setLoadingKey(crop.key);
    try {
      const { data } = await axios.get(`${API}/sample-data/${crop.sample_key}`, { responseType: 'text' });
      const csv = typeof data === 'string' ? data : await data.text();
      window.dispatchEvent(new CustomEvent('bk-load-dus-sample', {
        detail: {
          designKey,
          csv,
          filename: `dus_${crop.key}_demo.csv`,
          banner: `${crop.label} DUS demo loaded (${crop.n_descriptors} descriptors). Click Run to analyse.`,
        },
      }));
    } catch {
      toast.error('Could not load this crop sample. Please try again.');
    } finally {
      setLoadingKey(null);
    }
  };

  if (error) return null;

  return (
    <div className="mb-5 rounded-2xl overflow-hidden" data-testid="dus-crop-library"
      style={{ border: '1px solid var(--border-color)', background: 'linear-gradient(180deg,#F4FBFD 0%,#FFFFFF 60%)' }}>
      <div className="px-5 py-4 flex items-start gap-3" style={{ borderBottom: '1px solid rgba(8,145,178,0.14)' }}>
        <div className="flex items-center justify-center rounded-xl flex-shrink-0"
          style={{ width: 38, height: 38, background: 'rgba(8,145,178,0.12)' }}>
          <Sprout size={20} color={TEAL} />
        </div>
        <div className="flex-1">
          <div className="font-body" style={{ fontSize: 15, fontWeight: 700, color: INK }}>
            Crop descriptor library — PPV&amp;FR (India)
          </div>
          <p className="font-body mt-0.5" style={{ fontSize: 12.5, color: 'var(--text-medium)', lineHeight: 1.5 }}>
            Pick a crop to see its official qualitative &amp; pseudo-qualitative DUS descriptors and load a
            ready-made demo table. The analysis works on <strong>any</strong> descriptor data — these are
            standardised templates to speed up scoring.
          </p>
        </div>
      </div>

      {/* crop chips */}
      <div className="px-5 pt-4 flex flex-wrap gap-2">
        {crops.map(c => {
          const on = c.key === selected;
          return (
            <button key={c.key} data-testid={`dus-crop-chip-${c.key}`}
              onClick={() => setSelected(on ? null : c.key)}
              className="flex items-center gap-2 px-3.5 py-2 rounded-full transition-all font-body"
              style={{
                fontSize: 13, fontWeight: 600,
                border: `1px solid ${on ? TEAL : 'var(--border-color)'}`,
                background: on ? TEAL : 'white',
                color: on ? 'white' : 'var(--text-dark)',
              }}>
              <Leaf size={13} style={{ opacity: on ? 1 : 0.6 }} />
              {c.label}
              <span style={{
                fontSize: 10.5, fontWeight: 700, padding: '1px 6px', borderRadius: 20,
                background: on ? 'rgba(255,255,255,0.22)' : 'rgba(8,145,178,0.1)',
                color: on ? 'white' : TEAL,
              }}>{c.n_descriptors}</span>
            </button>
          );
        })}
        {crops.length === 0 && (
          <div className="flex items-center gap-2 py-2 font-body" style={{ fontSize: 13, color: 'var(--text-light)' }}>
            <Loader2 size={14} className="animate-spin" /> Loading crops…
          </div>
        )}
      </div>

      {/* descriptor reference for selected crop */}
      {active && (
        <div className="px-5 pb-5 pt-4" data-testid="dus-descriptor-reference">
          <div className="rounded-xl bg-white overflow-hidden" style={{ border: '1px solid var(--border-color)' }}>
            <div className="px-4 py-3 flex items-center justify-between flex-wrap gap-3"
              style={{ background: 'rgba(8,145,178,0.05)', borderBottom: '1px solid var(--border-color)' }}>
              <div className="flex items-center gap-2">
                <Table2 size={16} color={TEAL} />
                <span className="font-body" style={{ fontSize: 13.5, fontWeight: 700, color: INK }}>
                  {active.label}
                </span>
                <span className="font-body italic" style={{ fontSize: 12, color: 'var(--text-light)' }}>
                  {active.crop}
                </span>
              </div>
              <button data-testid={`dus-load-sample-${active.key}`}
                onClick={() => loadSample(active)} disabled={loadingKey === active.key}
                className="flex items-center gap-2 px-3.5 py-2 rounded-lg transition-all btn-press font-body"
                style={{ fontSize: 12.5, fontWeight: 700, background: TEAL, color: 'white', border: 'none',
                  cursor: 'pointer', opacity: loadingKey === active.key ? 0.7 : 1 }}>
                {loadingKey === active.key
                  ? <><Loader2 size={13} className="animate-spin" /> Loading…</>
                  : <><Download size={13} /> Load demo dataset</>}
              </button>
            </div>

            <div className="max-h-[360px] overflow-auto">
              <table className="w-full" style={{ borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
                    <th className="text-left font-body px-4 py-2" style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-light)', letterSpacing: '0.03em', borderBottom: '1px solid var(--border-color)' }}>CHARACTERISTIC</th>
                    <th className="text-left font-body px-3 py-2" style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-light)', letterSpacing: '0.03em', borderBottom: '1px solid var(--border-color)' }}>TYPE</th>
                    <th className="text-left font-body px-4 py-2" style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-light)', letterSpacing: '0.03em', borderBottom: '1px solid var(--border-color)' }}>STATES</th>
                  </tr>
                </thead>
                <tbody>
                  {active.descriptors.map((d, i) => (
                    <tr key={d.name} style={{ background: i % 2 ? 'rgba(8,145,178,0.025)' : 'white' }}>
                      <td className="font-body px-4 py-2 align-top" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-dark)', whiteSpace: 'nowrap' }}>
                        {d.name.replace(/_/g, ' ')}
                      </td>
                      <td className="px-3 py-2 align-top">
                        <span className="font-body" style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 7px', borderRadius: 5,
                          background: d.type === 'QL' ? 'rgba(16,122,87,0.12)' : 'rgba(180,110,20,0.12)',
                          color: d.type === 'QL' ? '#107A57' : '#B46E14' }}
                          title={TYPE_LABEL[d.type] || d.type}>
                          {d.type}
                        </span>
                      </td>
                      <td className="font-body px-4 py-2 align-top" style={{ fontSize: 12.5, color: 'var(--text-medium)' }}>
                        {d.states.join(', ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="px-4 py-2.5 flex items-start gap-2" style={{ background: 'rgba(8,145,178,0.04)', borderTop: '1px solid var(--border-color)' }}>
              <Info size={13} color={TEAL} style={{ marginTop: 2, flexShrink: 0 }} />
              <p className="font-body" style={{ fontSize: 11.5, color: 'var(--text-light)', lineHeight: 1.5 }}>
                Source: {active.source}. The demo dataset is a 12-accession template using these descriptors —
                replace the states with your own scored germplasm before analysing.
              </p>
            </div>
          </div>
        </div>
      )}

      {!active && crops.length > 0 && (
        <div className="px-5 pb-4 flex items-center gap-2 font-body" style={{ fontSize: 12, color: 'var(--text-light)' }}>
          <ChevronDown size={13} /> Select a crop above to view its descriptor checklist.
        </div>
      )}
    </div>
  );
}
